<script setup lang="ts">
import { Pet, BattleStatus } from "../core/pet";
import { Seer } from "../core/seer";
import { Side } from "../core/round";
import { _battle } from "../ui/battle";
import { computed } from "vue";
import { Tooltip } from "floating-vue";

const battle = _battle;
const props = defineProps<{ seer: Seer; side: Side }>();

const disabled = computed(
    () => battle.phase !== "selecting" || battle.viewMode !== "live"
);

/** 本方是否需要先补位（上回合出战精灵阵亡） */
const needReplacement = computed(() => battle.needsReplacement(props.side));
/** 本方已选择的补位精灵 */
const replacement = computed(() => battle.getReplacement(props.side));

function hpPercent(pet: Pet): number {
    return pet.最大体力 > 0
        ? Math.max(0, Math.min(100, pet.当前体力 / pet.最大体力 * 100))
        : 0;
}

function petLabel(pet: Pet): string {
    return `${pet.名字}，${statusLabel(pet.战斗状态)}，体力 ${pet.当前体力}/${pet.最大体力}${pet === replacement.value ? "，即将出战" : ""}`;
}

function isPending(pet: Pet) {
    const op = battle.getPending(props.side);
    return (op?.kind === "switch" && op.petId === pet.序号) || replacement.value === pet;
}

function onPetClick(pet: Pet) {
    if (needReplacement.value) {
        battle.selectReplacement(props.side, pet.序号);
    } else {
        battle.selectOperation(props.side, { kind: "switch", petId: pet.序号 });
    }
}

function statusLabel(status: BattleStatus): string {
    switch (status) {
        case BattleStatus.FIGHTING:
            return "出战";
        case BattleStatus.STANDBY:
            return "待命";
        case BattleStatus.DEAD:
            return "阵亡";
        default:
            return "";
    }
}

function statusClass(status: BattleStatus): string {
    switch (status) {
        case BattleStatus.FIGHTING:
            return "fighting";
        case BattleStatus.STANDBY:
            return "standby";
        case BattleStatus.DEAD:
            return "dead";
        default:
            return "";
    }
}
</script>

<template>
    <div class="pet-package">
        <Tooltip
            class="pet-slot"
            v-for="pet in seer.petPackage"
            :key="pet.序号"
        >
            <button
                type="button"
                @click="onPetClick(pet)"
                class="pet-btn"
                :aria-label="petLabel(pet)"
                :class="{
                    selected: isPending(pet),
                    upcoming: pet === replacement,
                }"
                :disabled="
                    disabled ||
                    pet === replacement ||
                    pet.战斗状态 === BattleStatus.FIGHTING ||
                    pet.战斗状态 === BattleStatus.DEAD
                "
            >
                <span :class="['pet-tag', statusClass(pet.战斗状态)]">{{ statusLabel(pet.战斗状态) }}</span>
                <span class="pet-avatar-wrap">
                    <img :src="pet.getHeadImgUrl()" alt="" />
                </span>
                <span class="pet-name">{{ pet.名字 }}</span>
                <span class="hp-track" aria-hidden="true">
                    <span
                        class="hp-fill"
                        :class="hpPercent(pet) > 50 ? 'high' : hpPercent(pet) > 25 ? 'medium' : 'low'"
                        :style="{ width: hpPercent(pet) + '%' }"
                    ></span>
                </span>
            </button>
            <template #popper>
                <span>{{ petLabel(pet) }}</span>
            </template>
        </Tooltip>
    </div>
</template>

<style scoped>
.pet-package {
    display: inline-flex;
    gap: calc(var(--ui-unit) * 8);
    max-width: 100%;
    overflow-x: auto;
    padding-block: calc(var(--ui-unit) * 3);
}

.pet-slot {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: calc(var(--ui-unit) * 4);
}

.pet-btn {
    position: relative;
    flex-direction: column;
    gap: calc(var(--ui-unit) * 5);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: calc(var(--ui-unit) * 82);
    padding: calc(var(--ui-unit) * 6) calc(var(--ui-unit) * 4);
    background: rgba(30, 30, 45, 0.9);
    border: calc(var(--ui-unit) * 1) solid rgba(255, 255, 255, 0.08);
    border-radius: calc(var(--ui-unit) * 8);
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(calc(var(--ui-unit) * 6));
}

.pet-btn:hover:not(:disabled) {
    background: rgba(40, 40, 60, 0.95);
    border-color: rgba(255, 215, 0, 0.35);
    transform: translateY(calc(var(--ui-unit) * -2));
    box-shadow: 0 calc(var(--ui-unit) * 4) calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.12);
}

.pet-btn.selected {
    border-color: #ffd700;
    box-shadow: 0 0 calc(var(--ui-unit) * 16) rgba(255, 215, 0, 0.25);
    transform: translateY(calc(var(--ui-unit) * -2));
    background: rgba(255, 215, 0, 0.06);
}

.pet-btn.upcoming {
    border-color: #3b82f6;
    box-shadow: 0 0 calc(var(--ui-unit) * 12) rgba(59, 130, 246, 0.3);
    background: rgba(59, 130, 246, 0.06);
}

.pet-btn:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    transform: none;
}

.pet-avatar-wrap {
    width: calc(var(--ui-unit) * 28);
    height: calc(var(--ui-unit) * 28);
    border-radius: calc(var(--ui-unit) * 8);
    overflow: hidden;
}

.pet-avatar-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.pet-tag {
    position: absolute;
    top: calc(var(--ui-unit) * 2);
    right: calc(var(--ui-unit) * 2);
    pointer-events: none;
    font-size: calc(var(--ui-unit) * 10);
    font-weight: 600;
    padding: calc(var(--ui-unit) * 1) calc(var(--ui-unit) * 2);
    border-radius: calc(var(--ui-unit) * 8);
    text-align: center;
}

.pet-tag.fighting {
    color: #f59e0b;
    background: rgba(245, 158, 11, 0.15);
}

.pet-tag.standby {
    color: #22c55e;
    background: rgba(34, 197, 94, 0.12);
}

.pet-tag.dead {
    color: #6b7280;
    background: rgba(107, 114, 128, 0.12);
}

.pet-name {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #e0e0e0;
    font-size: calc(var(--ui-unit) * 12);
    font-weight: 600;
    line-height: 1.1;
}

.hp-track {
    display: block;
    width: 100%;
    height: calc(var(--ui-unit) * 4);
    margin-top: calc(var(--ui-unit) * 2);
    border-radius: var(--ui-unit);
    overflow: hidden;
    background: rgba(255, 255, 255, 0.14);
}

.hp-fill { display: block; height: 100%; }
.hp-fill.high { background: #22c55e; }
.hp-fill.medium { background: #eab308; }
.hp-fill.low { background: #ef4444; }
</style>
